import { Link } from 'react-router-dom'
import { Heart, Wallet, Bell, ChevronRight, Check, Eye } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import { severityHeroTitle, severityIcon, severityTone } from '@/components/hearth'
import type { Alert, AlertType } from '@/types'

interface AttentionNeededStateProps {
  /** The alerts to display (shows the first/most important one prominently) */
  alerts: Alert[]
  /** Total count of unread alerts (may differ from alerts.length if paginated) */
  totalCount?: number
  /** Callback when "Review" is clicked on an alert */
  onReview?: (alert: Alert) => void
  /** Callback when "Looks fine" is clicked */
  onDismiss?: (alert: Alert) => void
  /** Whether a dismiss action is in progress */
  isDismissing?: boolean
  className?: string
}

const typeIcons: Record<AlertType, typeof Bell> = {
  HEALTH: Heart,
  FINANCIAL: Wallet,
  SYSTEM: Bell,
}

const typeLabels: Record<AlertType, string> = {
  HEALTH: 'Health',
  FINANCIAL: 'Money',
  SYSTEM: 'System',
}

/**
 * The "needs attention" hero. When something needs a look it becomes the
 * focus: a severity-tinted wash over the card, a plain-spoken headline
 * ("Worth a look"), the lead alert, and clear actions.
 */
export function AttentionNeededState({
  alerts,
  totalCount,
  onReview,
  onDismiss,
  isDismissing,
  className,
}: AttentionNeededStateProps) {
  if (alerts.length === 0) {
    return null
  }

  const primaryAlert = alerts[0]
  const TypeIcon = typeIcons[primaryAlert.alert_type]
  const SeverityIcon = severityIcon[primaryAlert.severity]
  const tone = severityTone[primaryAlert.severity]
  const alertCount = totalCount ?? alerts.length
  const hasMoreAlerts = alertCount > 1

  return (
    <div
      className={cn('hearth-card-floating overflow-hidden px-6 py-6', tone.wash, className)}
    >
      <div className="flex items-start gap-4">
        {/* Severity chip circle */}
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
            tone.chipBg,
            tone.fg
          )}
        >
          <SeverityIcon className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-xl font-semibold text-gray-900">
            {severityHeroTitle[primaryAlert.severity]}
          </h2>
          <p className="mt-1 text-sm font-medium text-gray-900">
            {hasMoreAlerts
              ? `${primaryAlert.title} — and ${alertCount - 1} more ${alertCount - 1 === 1 ? 'item' : 'items'}`
              : primaryAlert.title}
          </p>
          <p className="mt-0.5 line-clamp-2 text-sm text-gray-500">
            {primaryAlert.description}
          </p>
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500">
            <TypeIcon className="h-3.5 w-3.5" />
            <span>{typeLabels[primaryAlert.alert_type]}</span>
          </div>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {onReview && (
              <Button size="sm" onClick={() => onReview(primaryAlert)} className="gap-2">
                <Eye className="h-4 w-4" />
                Review
              </Button>
            )}
            {onDismiss && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDismiss(primaryAlert)}
                disabled={isDismissing}
                className="gap-2"
              >
                <Check className="h-4 w-4" />
                Looks fine
              </Button>
            )}
            {hasMoreAlerts && (
              <Link
                to="/alerts"
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
              >
                See all {alertCount} alerts
                <ChevronRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
